import { distinctUntilChanged, pairwise, startWith } from "rxjs";
import { inject, singleton } from "tsyringe";
import { ExtensionContext } from "../common/models/extension-config";
import { Module } from "../common/module";
import { Snippets } from "../common/snippets";
import { CommonModule } from "./common.module";
import { ApiModule } from "./common/api/api.module";
import { RequestAccessRequestDTO, RequestAccessResponseDTO, ResourceType } from "./common/api/autogenerated";
import { RequestInterceptorModule } from "./common/request-interceptor.module";
import { UrlChangeListenerModule } from "./common/url-change-listener.module";
@singleton()
export class RequestAccessModule extends Module {
    public name: string = 'RequestAccessModule';
    public acceptedLicenseFlags: string[] = ['RequestAccess'];
    public shouldRun(context: ExtensionContext): boolean {
        return context === ExtensionContext.vizportal;
    }
    constructor(
        @inject(CommonModule) private common: CommonModule,
        @inject(ApiModule) private apiModule: ApiModule,
        @inject(RequestInterceptorModule) private requestInterceptor: RequestInterceptorModule,
        @inject(UrlChangeListenerModule) private urlChangeListener: UrlChangeListenerModule) {
        super([common, apiModule, urlChangeListener, requestInterceptor]);
    }

    public async run(): Promise<void> {
        this.urlChangeListener.event.pipe(startWith(''), distinctUntilChanged(), pairwise()).subscribe(e => this.getAccessGroups(e))
        
    }

    private counter = 0;
    private async getAccessGroups(urls: string[]) {
        const curCounter = ++this.counter;
        // find the button (both in old and new UI)
        // urls is an array, 0 is last URL, 1 is current. We might need last URL as new UI redirects and shows a popup and last URL contains the stuff we need
        const btn = await this.common.waitForElement('[data-tb-test-id=request-access-submit-btn-Button],[data-tb-test-id=request-access-request-access-button-Button]', 5, document);
        if (!btn || this.counter !== curCounter) {
            return;
        }
        this.log.i('Found Request Access button');

        let urlData: { type: ResourceType, resourceId?: number, contentUrl?: string} | undefined;
        // try to get URL data from current URL (old UI)
        urlData = this.getUrlData(urls[1]);
        if (!urlData) {
            // try to get URL data from last URL (new UI)
            urlData = this.getUrlData(urls[0] || '');
        }
        if (!urlData) {
            this.log.w('URL data not found in old or new URL');
            return;
        }
       
        // try to get dialog footer (new UI) and remove
        const footer = document.querySelector('[data-tb-test-id=request-access-Dialog-Footer]');
        if (footer) {
            footer.remove()
        }
        // try to get message (optional) area (new UI) and remove
        const textarea = document.querySelector('[data-tb-test-id=request-access-text-area-TextArea]');
        if (textarea?.parentElement) {
            textarea.parentElement.remove();
        }
        // remove the button
        btn.remove();

        // get the message div and show the loader
        const messageDiv = document.querySelector('.tb-error-box-message-second,[data-tb-test-id=request-access-explanatory-text]');
        messageDiv.innerHTML = Snippets.loading;

        const getGroups = await this.apiModule.apiRequest(e => e.apiRequestAccessTableauIntegrationRequestAccess(new RequestAccessRequestDTO({
            serverId: window.stm.config.serverId,
            type: urlData.type,
            contentURL: urlData.contentUrl,
            resourceId: urlData.resourceId
        })));
        if (!getGroups.success) {
            messageDiv.innerText = 'Failed to retrieve Admin Groups for resource. Please manually request access from content owners.';
            this.log.e('Failed to get Request Access Groups', getGroups.error);
            return;
        }
        if (!getGroups.value?.permissionGroups?.length) {
            // we don't know what Groups to use
            messageDiv.innerText =  'Failed to retrieve Admin Groups for resource. Please manually request access from content owners.';
            return;
        }
        const getView = await this.apiModule.htmlRequest(e => e.apiTableauIntegrationRequestAccess(getGroups.value));
        if (!getView.success) {
            messageDiv.innerText = 'Failed to retrieve Admin Groups HTML for resource. Please manually request access from content owners.';
            this.log.e('Failed to get Request Access Groups HTML', getGroups.error);
            return;
        }
        messageDiv.innerHTML = '';
        messageDiv.appendChild(getView.value);

    }

    private getUrlData(url: string): { type: ResourceType, resourceId?: number, contentUrl?: string} | undefined {
        const regexProject = "(\/projects\/([^\/]+))";
        const regexWorkbook = "(\/workbooks\/([^\/]+))";
        const regexView = "(\/views\/([^\/]+\/[^\/?]+))";

        var isProject = url.match(regexProject);
        if (isProject) {
            return {
                type: ResourceType.Project,
                resourceId: +isProject[2]
            };
        }

        var isWorkbook = url.match(regexWorkbook);
        if (isWorkbook) {
            return {
                type: ResourceType.Workbook,
                resourceId: +isWorkbook[2]
            };
        }

        var isView = url.match(regexView);
        if (isView) {
            return {
                type: ResourceType.View,
                contentUrl: isView[2]
            };
        }
        return undefined;

    }

    private 



}